import React from 'react';
import { Button } from '@/components/ui/Button';
import { useCounterOfferValidation } from '@/hooks/useCounterOfferValidation';
import { useCounterOfferStore } from '@/stores/counterOfferStore';
import { PoolDetail } from '@/types/pool';
import { formatCurrency } from '@/utils/misc';
import { formatTermDisplay } from '@/utils/poolCreateUtils';
import { CurrencyField } from '../../ui/CurrencyField';
import { PercentageField } from '../../ui/PercentageField';
import { TextField } from '../../ui/TextField';
import { ComparisonCalculator } from '../forms/PoolTermsStep/ComparisonCalculator';
import { InfoField } from './InfoField';

type CounterOfferPoolTermsStepProps = {
  pool: PoolDetail;
};

export const CounterOfferPoolTermsStep: React.FC<CounterOfferPoolTermsStepProps> = ({ pool }) => {
  const counterAmount = useCounterOfferStore((state) => state.counterAmount);
  const counterRate = useCounterOfferStore((state) => state.counterRate);
  const counterTermMonths = useCounterOfferStore((state) => state.counterTermMonths);

  const setCounterAmount = useCounterOfferStore((state) => state.setCounterAmount);
  const setCounterRate = useCounterOfferStore((state) => state.setCounterRate);
  const setCounterTermMonths = useCounterOfferStore((state) => state.setCounterTermMonths);
  const nextStep = useCounterOfferStore((state) => state.nextStep);
  const previousStep = useCounterOfferStore((state) => state.previousStep);
  const isFirstStep = useCounterOfferStore((state) => state.isFirstStep);

  const { validate, errors } = useCounterOfferValidation();

  const handleTermChange = (value: string) => {
    const monthValue = parseInt(value, 10);
    if (!isNaN(monthValue)) {
      setCounterTermMonths(monthValue);
    }
  };

  return (
    <div className="p-8 flex flex-col gap-6 max-h-[70vh] overflow-y-auto">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-6 items-start">
          <InfoField label="Pool Amount" value={formatCurrency(pool.loan_terms.amount)} size="large" />
          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-600 font-avenir">Enter the amount ($) you are offering.</label>
            <CurrencyField value={counterAmount} onChange={setCounterAmount} error={!!errors.amount} placeholder="0" />
            {errors.amount && <span className="text-xs text-[#DC2626] font-avenir">{errors.amount}</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 items-start">
          <InfoField label="Interest rate" value={`% ${pool.loan_terms.roi_rate}`} size="large" />
          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-600 font-avenir">Enter the annual return (%) you are offering.</label>
            <PercentageField value={counterRate} onChange={setCounterRate} error={!!errors.rate} placeholder="0" />
            {errors.rate && <span className="text-xs text-[#DC2626] font-avenir">{errors.rate}</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 items-start">
          <InfoField label="Term" value={formatTermDisplay(pool.loan_terms.term_months)} size="large" />
          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-600 font-avenir">Enter the counter offer term (months).</label>
            <TextField
              value={counterTermMonths.toString()}
              onChange={handleTermChange}
              placeholder="Enter months"
              type="number"
              inputMode="numeric"
              error={!!errors.term}
            />
            {errors.term && <span className="text-xs text-[#DC2626] font-avenir">{errors.term}</span>}
          </div>
        </div>
      </div>

      {/* Calculator Section */}
      <ComparisonCalculator />

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200">
        {!isFirstStep() && (
          <Button onClick={previousStep} variant="secondary">
            Previous
          </Button>
        )}
        <Button
          onClick={() => {
            if (validate()) {
              nextStep();
            }
          }}
          variant="primary"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
